import { format } from 'date-fns';
import { snakeCase } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnRecordingsAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { TimelineService } from '@lib/timeline';
import { t } from '@lib/translation';

@Component({
  selector: 'fs-vault-dialog',
  templateUrl: './vault-dialog.component.html',
  styleUrls: ['./vault-dialog.component.scss'],
})
export class SnVaultDialogComponent implements OnInit, OnDestroy {
  public form: UntypedFormGroup = this.formBuilder.group({
    notifyByText: [false],
    notifyByEmail: [false],
  });
  public formFields: SnFormFieldConfig[];
  public formModel: any = {};

  public isLoading = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly _matDialogRef: MatDialogRef<SnVaultDialogComponent>,
    private readonly _timelineService: TimelineService,
    private readonly _toastrService: ToastrService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly _recordingAPI: SnRecordingsAPI,
  ) {}

  public ngOnInit(): void {
    if (this.data.id) {
      return;
    }
    this._initForm();
    this.form = this.formBuilder.group({
      notifyByText: [false],
      notifyByEmail: [false],
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSubmit(): void {
    this.isLoading = true;
    const { id } = this.data;
    const notifyByText = this.form.get('notifyByText').value;
    const notifyByEmail = this.form.get('notifyByEmail').value;
    let request$: Observable<any> | null = null;

    if (id) {
      request$ = this._recordingAPI.retryClip(id, {
        notifyByText,
        notifyByEmail,
      });
    } else {
      const { cameraId, start, end, vaultIncidents } = this.data;
      const name = this.formFields?.[0].formControl.value;
      if (!name) {
        this._toastrService.error(t('vault.videoNameNotFound'));
        return;
      }
      const fileName = snakeCase(name);
      request$ = this._timelineService.addToVault(
        cameraId,
        start,
        end,
        vaultIncidents,
        notifyByText,
        notifyByEmail,
        fileName,
      );
    }
    this._toastrService.overlayContainer = this.data?.playerRef?.toastContainer;
    if (!request$) {
      return;
    }
    request$.pipe(takeUntil(this._destroy$)).subscribe({
      next: () => {
        this._toastrService.success(t('vault.clipAvailableSoon'));
        this.isLoading = false;
        this._matDialogRef.close(true);
      },
      error: () => {
        this._toastrService.error(t('vault.unableToAddToVault'));
        this.isLoading = false;
      },
    });
  }

  public cancel(): void {
    this._matDialogRef.close(false);
  }

  private _initForm(): void {
    this.formFields = [
      {
        key: 'fileName',
        type: 'input',
        templateOptions: {
          label: t('map.vaultDialog.fileName'),
          required: true,
        },
      },
    ];
  }
}
